// İSG uzmanı portalı — giriş ekranı. RHF + Zod validasyon; TanStack Query mutation
// ile `/auth/login`; hata i18n (errorCode → metin). Başarıda panele yönlenir.

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Input,
  Spinner,
  login,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const schema = useMemo(
    () =>
      z.object({
        email: z.string().min(1, t('validation.emailRequired')).email(t('validation.emailInvalid')),
        password: z.string().min(1, t('validation.passwordRequired')),
      }),
    [t],
  );

  type FormValues = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) => login(values),
    onSuccess: () => navigate('/', { replace: true }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle as="h1">{t('login.title')}</CardTitle>
        {/* `carbon:mb-6` (S33-8 review fix #8, 1.5rem / Carbon spacing-06) — `CardHeader`/
            `CardContent`'in `carbon:p-0`/`carbon:gap-0`'ı (dış `Card` TEK dolgu kaynağı, bkz.
            `Card.tsx` dosya başlığı) alt başlıkla ilk alan etiketi arasında 0px bırakıyordu. */}
        <CardDescription className="carbon:mb-6">{t('login.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={handleSubmit((values) => mutation.mutate(values))}
          noValidate
        >
          {mutation.isError ? (
            <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
          ) : null}

          <Field htmlFor="email" label={t('login.email')} error={errors.email?.message} required>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder={t('login.emailPlaceholder')}
              invalid={Boolean(errors.email)}
              {...register('email')}
            />
          </Field>

          <Field
            htmlFor="password"
            label={t('login.password')}
            error={errors.password?.message}
            required
          >
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              invalid={Boolean(errors.password)}
              {...register('password')}
            />
          </Field>

          <Button type="submit" loading={mutation.isPending} className="mt-1 w-full">
            {mutation.isPending ? <Spinner label={t('login.submit')} /> : t('login.submit')}
          </Button>

          <div className="text-center text-sm">
            <Link to="/forgot-password" className="text-primary hover:underline">
              {t('login.forgotPassword')}
            </Link>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
